import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { useAutoLogout } from '@/hooks/useAutoLogout';
import { playClickSound } from '@/utils/soundUtils';
import { MODAL_TOAST_MAX_WIDTH } from '@/styles/modalTokens';
import { Button } from '@/components/ui';
import { typeRole } from '@/styles/typography';

function formatTimeRemaining(ms: number, format: 'minutes' | 'seconds'): string {
  if (format === 'seconds') {
    return `${Math.ceil(ms / 1000)}s`;
  }
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

export default function AutoLogoutWarning() {
  const { timeRemaining, isWarningVisible, resetTimer, format } = useAutoLogout();

  if (!isWarningVisible || timeRemaining <= 0) {
    return null;
  }

  const handleStayActive = () => {
    playClickSound();
    resetTimer();
  };

  return (
    <div
      className="fixed top-4 right-4 z-[60] transform transition-all duration-300 ease-in-out"
      style={{ maxWidth: MODAL_TOAST_MAX_WIDTH }}
    >
      <div
        className={`
          bg-gradient-to-br from-red-500 via-red-600 to-rose-700
          border-2 border-red-400/50
          rounded-lg shadow-2xl p-4
          backdrop-blur-md
          animate-pulse
        `}
      >
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <h3 className={`text-white font-bold ${typeRole.input}`}>
                Auto-Logout Warning
              </h3>
            </div>
            <p className={`text-white/90 ${typeRole.body} mb-3`}>
              Logging out in: <span className={`font-bold ${typeRole.header1} text-money-out-soft`}>{formatTimeRemaining(timeRemaining, format)}</span>
            </p>
            <Button variant="tertiary" size="sm" fullWidth onClick={handleStayActive}>
              Stay Active
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
